import { parseIngredientLine } from "@household/shared/ingredient-line";
import { currencyFormat, dateTimeFormat, getResolvedLocale, numberFormat, parseLocaleNumber, relativeTimeFormat, type TypedIn } from "./locale.js";
import type { MealFilters, MealSuggestion, ShoppingList } from "./types.js";

/**
 * The list a "put this on the shopping list" action targets.
 *
 * Every food surface targets a grocery list, never a project's materials list
 * (#465 kind). A list with no kind predates #465 and is groceries. Among the
 * grocery lists, the household's marked default wins (#1679); with none marked,
 * the most recently updated one — the list actually in use — is the answer,
 * never API order. This mirrors the server's `findDefaultGroceryList`, which
 * is what the assistant's shopping-add proposals resolve through, so the
 * recipe page, the Things panel, Food and the assistant all name the same list.
 *
 * Here rather than inline in App.tsx because the Things panel needs the same
 * answer (#1198) and reaches it from a list it fetched itself — the shell only
 * loads shopping lists on the tabs that show them, so a household that opened
 * Things directly has none in hand. Two copies of this rule is how one surface
 * comes to add to a materials list.
 */
export function activeGroceryList(lists: readonly ShoppingList[]): ShoppingList | undefined {
  let best: ShoppingList | undefined;
  for (const list of lists) {
    if (list.kind === "project") continue;
    if (list.isDefault) return list;
    if (!best || (list.updatedAt ?? "") > (best.updatedAt ?? "")) best = list;
  }
  return best;
}

export function money(minor?: number | null, currency = "USD") {
  const amount = (minor ?? 0) / 100;
  try {
    return currencyFormat(getResolvedLocale(), currency).format(amount);
  } catch {
    // Intl throws RangeError on an invalid currency code. Fall back to a plain
    // number plus the raw code rather than crashing the whole view.
    return `${amount.toFixed(2)} ${String(currency).toUpperCase()}`.trim();
  }
}

export function formValue(form: HTMLFormElement, name: string) {
  return String(new FormData(form).get(name) ?? "").trim();
}

/** "1 place" / "3 places" — count with a correctly singular/plural noun. */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Audit action slugs are raw DB enum values ("assistant_turn", "post_finance").
 * Strip the snake_case so they can be shown to a person (#864). Lives here
 * rather than in a feature so the Dashboard activity feed and the per-entity
 * audit trail render the same slug the same way.
 */
export function readableAction(value: string): string {
  return value.replace(/_/g, " ");
}

export function assetSearchPath(query: string) {
  return `/api/v1/assets${query ? `?q=${encodeURIComponent(query)}` : ""}`;
}

export function optionalNumber(value: string) {
  return value ? Number(value) : null;
}

export function optionalId(value: string) {
  return value || null;
}

export function amountMinor(value: string) {
  return Math.round(Number(value || "0") * 100);
}

/**
 * The minor-unit integer behind an OPTIONAL money field, or null when it is
 * blank.
 *
 * Deliberately not `amountMinor`, which answers 0 for an empty string. Where a
 * field is genuinely optional — a thing's estimated value, what it cost — that
 * difference is the whole point: "" means nobody has said what this is worth,
 * and storing 0 instead valuates every unpriced thing in the household at
 * nothing, which then reads as a real figure in any rollup (#1181).
 *
 * The argument is the `.value` of an `<input type="number">`, which is
 * dot-decimal by spec in every locale, so this must NOT go through
 * `parseLocaleNumber` — see the rule in locale.ts. Text the browser cannot read
 * as a number reaches here as "", and its own `badInput` validity blocks the
 * submit before that.
 */
export function optionalAmountMinor(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

/**
 * A stored minor-unit amount written back INTO an `<input type="number">`.
 *
 * Two fixed decimals and a decimal POINT whatever the reader's language: the
 * element's value is dot-decimal by spec, and writing a locale-formatted
 * "1.234,56" into it makes the browser blank the field — which on an edit form
 * turns "open a thing and save it" into "clear its value".
 */
export function amountInputValue(minor: number | null | undefined): string {
  return minor == null ? "" : (minor / 100).toFixed(2);
}

export function todayMonth() {
  return new Date().toISOString().slice(0, 7);
}

/**
 * The leading amount of an ingredient line, rewritten in the convention the
 * shared parser reads.
 *
 * `parseIngredientLine` lives in the shared package — the API and the assistant
 * read the same lines — and its grammar is dot-decimal. Handing it Spanish text
 * destroyed the ingredient: "1.500 g harina" (one thousand five hundred grams)
 * came back as 1.5 g, and "1,5 tazas arroz" matched no amount at all, so the
 * whole line was stored as the NAME with its quantity and unit gone.
 *
 * ONLY the leading token, and only when it is entirely digits and separators.
 * Rewriting separators anywhere in a line was tried and was wrong — a separator
 * between two digits is not necessarily a number, and "Modelo 1.234" is a name.
 * Anchoring at the start is what makes it safe: by the parser’s own grammar the
 * token before the first space IS the amount. A fraction ("1 1/2") is left
 * alone — the shared grammar reads those itself, and they carry no separator to
 * misread.
 */
function amountInParserConvention(line: string, typedIn: TypedIn): string {
  if (typedIn === "en") return line;
  const match = line.match(/^([0-9][0-9.,]*)(\s+.*)$/);
  if (!match?.[1] || !match[2]) return line;
  const value = parseLocaleNumber(match[1], typedIn);
  return value == null ? line : `${value}${match[2]}`;
}

/**
 * @param typedIn the language the textarea was written in — not the app’s
 * current one. A draft can outlive a language change that syncs in from another
 * device, and the text is still written in the language it was typed in.
 */
export function parseIngredients(value: string, typedIn: TypedIn) {
  return value.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const parsed = parseIngredientLine(amountInParserConvention(line, typedIn));
    if (parsed.quantity == null) return { name: parsed.name };
    return { quantity: parsed.quantity, unit: parsed.unit, name: parsed.name };
  });
}

export function mealSourceLabel(sourceType: MealSuggestion["sourceType"]) {
  if (sourceType === "saved_recipe") return "Saved recipe";
  if (sourceType === "catalog_recipe") return "Catalog";
  if (sourceType === "simple_meal") return "Simple dinner";
  if (sourceType === "llm") return "LLM idea";
  return "Flexible meal";
}

export function ingredientText(name: string, quantity?: number | null, unit?: string | null) {
  const amount = quantity ? `${quantity} ${unit ?? ""}`.trim() : "";
  return amount ? `${amount} ${name}` : name;
}

export function mealFilterQuery(filters: MealFilters) {
  const params = new URLSearchParams({
    maxMissingItems: String(filters.maxMissingItems),
    includeStaples: String(filters.includeStaples),
    prioritizeExpiring: String(filters.prioritizeExpiring),
    simpleMealsOnly: String(filters.simpleMealsOnly),
    recipesOnly: String(filters.recipesOnly),
    includeCatalog: String(filters.includeCatalog),
    includeLlm: String(filters.includeLlm),
    includePending: String(filters.includePending)
  });
  if (filters.maxCookMinutes) params.set("maxCookMinutes", filters.maxCookMinutes);
  if (filters.tags.trim()) params.set("tags", filters.tags.trim());
  return params.toString();
}

export function localDateOnly(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Adds whole days to a "YYYY-MM-DD" string, returning "" for malformed input. */
export function addDays(dateOnly: string, days: number) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return "";
  const date = new Date(`${dateOnly}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function browserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * Today as the reader would write it, "YYYY-MM-DD".
 *
 * `new Date().toISOString().slice(0, 10)` is the obvious version and it is
 * wrong for most of the world for part of every day: at 20:20 in Boston the UTC
 * date has already rolled over, so anything dated from it is dated tomorrow.
 * The server side of that lesson is `localDateInTimeZone` (#1200); this is the
 * browser's, and it is here rather than imported because the shared root barrel
 * must not be pulled into the web bundle.
 *
 * `en-CA` renders exactly YYYY-MM-DD, and it is pinned rather than taken from
 * the reader's locale precisely so the output shape never follows the reader.
 */
export function browserDateOnly(now: Date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: browserTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(now);
}

/**
 * An attachment size, in the reader's number format.
 *
 * The NUMBER goes through Intl; the unit does not. Intl's own byte units
 * render "byte" and "kB" where this app has always shown "B" and "KB", so
 * adopting them would rename units across every attachment row to fix a
 * decimal separator. Only the separator was wrong.
 */
export function fileSize(bytes: number) {
  if (bytes < 1024) return `${numberFormat().format(bytes)} B`;
  if (bytes < 1024 * 1024) return `${numberFormat().format(Math.round(bytes / 1024))} KB`;
  const mb = numberFormat({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${mb.format(bytes / (1024 * 1024))} MB`;
}

export function exactTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return dateTimeFormat({
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

/**
 * "3 minutes ago", in the reader's language.
 *
 * This used to return hardcoded English built from millisecond arithmetic and
 * an "s" appended for plurals — and it is the DEFAULT of the Chores activity
 * feed's timestamp toggle, so a Spanish reader met "2 weeks ago" beside Spanish
 * activity text until they tapped it, whereupon its sibling exactTimestamp
 * (which always did go through Intl) answered in Spanish.
 *
 * The thresholds are unchanged; only the rendering moved to Intl, which also
 * supplies the plural rules and the idiomatic "yesterday"/"ayer" form.
 *
 * One deliberate simplification: the old code produced a composite "2 years 3
 * months ago" past a year. RelativeTimeFormat has no composite form, and the
 * two-call alternative reads as nonsense in any language, so anything past a
 * year now rounds to years. On a relative stamp that precision was never
 * meaningful.
 */
export function relativeTimestamp(value: string, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const relative = relativeTimeFormat();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 45_000) return relative.format(0, "second");
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return relative.format(-minutes, "minute");
  const hours = Math.round(diffMs / 3_600_000);
  if (hours < 24) return relative.format(-hours, "hour");
  const days = Math.round(diffMs / 86_400_000);
  if (days < 7) return relative.format(-days, "day");
  const weeks = Math.round(days / 7);
  if (weeks < 5) return relative.format(-weeks, "week");
  const months = Math.floor(days / 30);
  if (months < 12) return relative.format(-months, "month");
  return relative.format(-Math.floor(months / 12), "year");
}

// The batch task-occurrence endpoints accept at most 100 ids per request
// (taskOccurrenceBatch*RequestSchema). Nothing bounds how many occurrences a
// caller holds: the dashboard occurrence query has no LIMIT, so a recurring
// chore missed for months puts hundreds of pending occurrences behind a single
// card, and "Skip all" / "Done all" / "Snooze all" would 400 without changing
// anything. Every batch caller splits its ids through this and sends the
// requests in order. Each request is its own write transaction, so a mid-run
// failure stops there and leaves the remaining occurrences untouched.
export const OCCURRENCE_BATCH_LIMIT = 100;

export function occurrenceBatches<T>(ids: T[]): T[][] {
  const batches: T[][] = [];
  for (let index = 0; index < ids.length; index += OCCURRENCE_BATCH_LIMIT) {
    batches.push(ids.slice(index, index + OCCURRENCE_BATCH_LIMIT));
  }
  return batches;
}
